// ===========================================
// Edit one student's start week
// ===========================================
// Saves through `useUpdateEnrollment` (PATCH /lessons/:id/enrollments/:studentId). A later start
// over weeks that already hold attendance / bookings is REFUSED by the server (409
// WINDOW_HAS_DATA) and answered with WindowHasDataDialog, not a toast.

import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { LessonEnrollment } from '../../../api/lessons.api';
import { useUpdateEnrollment } from '../../../hooks/useLessons';
import { useHistoryEntryState } from '../../../hooks/useHistoryEntryState';
import { useBillingNotice } from '../../../hooks/useBillingNotice';
import { effectiveStartWeek, lessonWeekWindow, termWeekCount } from '../../../utils/dateUtils';
import type { InvoiceToAmend } from '../../../api/invoices.api';
import InvoicesToAmendDialog from '../../invoices/InvoicesToAmendDialog';
import StartWeekSelect, { StartWeekLesson, StartWeekTerm } from './StartWeekSelect';
import WindowHasDataDialog, { parseWindowHasData, WindowHasDataInfo } from '../lessons/WindowHasDataDialog';

interface EditEnrollmentStartWeekDialogProps {
  open: boolean;
  onClose: () => void;
  lessonId: string;
  lesson: StartWeekLesson;
  term: StartWeekTerm;
  enrollment: LessonEnrollment | null;
}

export default function EditEnrollmentStartWeekDialog({
  open,
  onClose,
  lessonId,
  lesson,
  term,
  enrollment,
}: EditEnrollmentStartWeekDialogProps) {
  const update = useUpdateEnrollment();
  const billingNotice = useBillingNotice();
  const [startWeek, setStartWeek] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<WindowHasDataInfo | null>(null);
  // Sent invoices that MAY need amending after a saved change (D2: a prompt, never automatic).
  // Kept with this history entry, so Back from an invoice brings the list back (D-c).
  const [amendPrompt, setAmendPrompt] = useHistoryEntryState<{
    invoices: InvoiceToAmend[];
    previousStart: number;
    newStart: number;
    studentId: string;
    studentName: string;
  } | null>(`enrolment-amend-prompt:${lessonId}`, null);

  // Seed with what the select will SHOW. A stored start at or before the class's own start
  // (the class start was moved later after the student enrolled) displays as "class start";
  // seeding the raw value would Save a week the admin never saw, and the server 400s it (/qa F1).
  const classStart = lessonWeekWindow(lesson, termWeekCount(term.startDate, term.endDate)).first;
  useEffect(() => {
    if (!open) return;
    const stored = enrollment?.startWeek ?? null;
    setStartWeek(stored !== null && stored > classStart ? stored : null);
  }, [open, enrollment, classStart]);

  const save = async () => {
    if (!enrollment) return;
    try {
      const result = await update.mutateAsync({ lessonId, studentId: enrollment.studentId, startWeek });
      const invoices = result?.invoicesToAmend ?? [];
      billingNotice('window', result?.invoicesMayNeedAttention);
      if (invoices.length > 0) {
        setAmendPrompt({
          invoices,
          previousStart: effectiveStartWeek(lesson, enrollment),
          newStart: effectiveStartWeek(lesson, { startWeek }),
          studentId: enrollment.studentId,
          studentName: `${enrollment.Student.firstName} ${enrollment.Student.lastName}`,
        });
      }
      onClose();
    } catch (err) {
      const refused = parseWindowHasData(err);
      if (refused) setRefusal(refused);
      // Any other error was toasted by the hook.
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="edit-start-week-title">
        <DialogTitle id="edit-start-week-title">
          Starting week{enrollment ? ` — ${enrollment.Student.firstName} ${enrollment.Student.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The week this student joins the class.
          </Typography>
          <StartWeekSelect
            id="edit-enrol-start-week"
            lesson={lesson}
            term={term}
            value={startWeek}
            onChange={setStartWeek}
            disabled={update.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={update.isPending || !enrollment}>
            {update.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <WindowHasDataDialog info={refusal} onClose={() => setRefusal(null)} />
      {amendPrompt && (
        <InvoicesToAmendDialog
          open
          onClose={() => setAmendPrompt(null)}
          invoices={amendPrompt.invoices}
          context={{
            kind: 'student',
            lessonId,
            studentId: amendPrompt.studentId,
            studentName: amendPrompt.studentName,
            lessonName: lesson.name ?? 'this class',
            termName: term.name ?? 'this term',
            newStartWeek: amendPrompt.newStart,
            previousStartWeek: amendPrompt.previousStart,
          }}
        />
      )}
    </>
  );
}
